import { api, enviarFormulario } from '../api';

/**
 * As chamadas do triaxial, com os caminhos escritos por extenso — o
 * teste `26` precisa enxergar a string literal.
 */

export interface EnsaioTriaxial {
  cabecalho: any;
  corpos: any[];
  rupturas: any[];
  envoltoria: any | null;
  /** Todos os critérios de ruptura, lado a lado, por CP (0235). */
  criterios?: any[];
  /** Uma envoltória por critério de ruptura, para comparar c e φ (0235). */
  envoltorias?: any[];
  /** O item de medição do ensaio — no pacote, quantos CPs (0210). */
  pacote?: { item_codigo: string; cps_por_ensaio: number | null;
             no_escopo: boolean } | null;
  /** As balanças das duas baterias (RN-215, onda 238). */
  balanca?: { id: string; codigo: string; nome: string } | null;
  balancaUmidade?: { id: string; codigo: string; nome: string } | null;
}

export interface CorpoDeProva {
  cp: any;
  umidade: any[];
  saturacao: any[];
  adensamento: any[];
  cisalhamento: any[];
  ruptura: any | null;
  /** Todos os critérios de ruptura deste CP, lado a lado (0235). */
  criterios?: any[];
}

export interface ConferenciaTriaxial {
  simulado: boolean;
  arquivo: {
    identificacao: string | null;
    numeroTeste: number | null;
    camara: string | null;
    diametroMm: number | null;
    alturaMm: number | null;
  };
  corpoDeProva: { identificacao: string; camara: string | null };
  saturacao: number;
  adensamento: number;
  cisalhamento: number;
  degraus: {
    ordem: number;
    contrapressaoKpa: number;
    parametroB: number | null;
    parametroBDoEquipamento: number | null;
  }[];
  corposNoArquivo: string[];
  avisos: string[];
  gravado?: { saturacao: number; adensamento: number; cisalhamento: number };
}

export const verTriaxial = (id: string) =>
  api<EnsaioTriaxial>(`/triaxial/${id}`);

export const verTriaxialCp = (cpId: string) =>
  api<CorpoDeProva>(`/triaxial/cp/${cpId}`);

export const gravarTriaxialFicha = (id: string, corpo: unknown) =>
  api<any>(`/triaxial/${id}/ficha`, { metodo: 'POST', corpo });

export const criarTriaxialCp = (id: string, corpo: unknown) =>
  api<any>(`/triaxial/${id}/corpos`, { metodo: 'POST', corpo });

export const editarTriaxialCp = (cpId: string, corpo: unknown) =>
  api<CorpoDeProva>(`/triaxial/cp/${cpId}`, { metodo: 'PATCH', corpo });

export const gravarTriaxialUmidade = (cpId: string, corpo: unknown) =>
  api<CorpoDeProva>(`/triaxial/cp/${cpId}/umidade`,
    { metodo: 'POST', corpo });

export const descartarTriaxialCapsula = (
  cpId: string, uid: string, motivo: string,
) => api<CorpoDeProva>(`/triaxial/cp/${cpId}/umidade/${uid}/descartar`,
  { metodo: 'POST', corpo: { motivo } });

/** As envoltórias por critério que vão para o relatório final (0235). */
export const gravarCriteriosNoLaudo = (id: string, criterios: string[]) =>
  api<any[]>(`/triaxial/${id}/criterios-no-laudo`,
    { metodo: 'POST', corpo: { criterios } });

export const verTriaxialEnvoltoria = (id: string) =>
  api<{ envoltoria: any | null; pontos: any[] }>(
    `/triaxial/${id}/envoltoria`);

/**
 * Importa o bruto da prensa para UM corpo de prova.
 *
 * `simular` traz o que o arquivo tem sem gravar nada — é a conferência
 * que o controlador explica: trazer três etapas e só então descobrir que
 * o arquivo era do CP vizinho é o erro que não se desfaz num banco que
 * não apaga.
 */
export function importarBrutoTriaxial(
  cpId: string,
  arquivo: File,
  opcoes: { simular?: boolean; corpoDoArquivo?: string } = {},
  aoProgredir?: (f: number) => void,
): Promise<ConferenciaTriaxial> {
  const form = new FormData();
  form.append('arquivo', arquivo);
  if (opcoes.simular) form.append('simular', 'true');
  if (opcoes.corpoDoArquivo) {
    form.append('corpoDoArquivo', opcoes.corpoDoArquivo);
  }
  return enviarFormulario<ConferenciaTriaxial>(
    `/triaxial/cp/${cpId}/importar`, form, aoProgredir);
}
